/**
 * lib/liveTimetable.ts
 * ------------------------------------------------------------------
 * The timetable AT THIS MOMENT, as the Timetable screen's "My day" and
 * "School now" views draw it (the school chose, 2026-09-29: a teacher gets
 * their own day with the lesson on now; an admin gets a board of every class
 * with a staff tab). Ported in spirit from the authoring app's HomeView and
 * utils/livePeriod.ts, which do the same for the timetable office.
 *
 * Everything is pure and tested (liveTimetable.test.ts); the screen only draws.
 *
 * THE CLOCK IS GABORONE'S (UTC+2, no daylight saving), not the device's: a
 * teacher whose laptop is on another timezone must still be told the right
 * period. `schoolClock()` is the one place that conversion happens.
 *
 * THE BELL IS PER DAY. Monday to Thursday break after period 6 and Friday
 * after period 4, so a period number means nothing without its day — every
 * lookup here takes both.
 * ------------------------------------------------------------------ */
import {
  DAYS, dayForDate, nextTeachingDay, isTeachingSlot, parsePointerKey, slotGroups, slotRooms, slotTeacherIds,
  type Day, type PeriodSlot, type PointerKey, type ScheduleSettings, type SlotGroup,
  type SlotId, type TimetablePointer, type TimetableSlot,
} from '@/lib/timetable';
import type { ExamPaperDuties } from '@/lib/examDuties';

/* ============================ the clock ============================ */

export interface SchoolClock {
  /** Today's cycle day (lib/teachingCycle), or null on a day without teaching. */
  day: Day | null;
  /** Minutes past midnight, Gaborone. */
  minutes: number;
  /** 'YYYY-MM-DD', Gaborone. */
  ymd: string;
}

const GABORONE_OFFSET_MIN = 120;

export function schoolClock(now: Date): SchoolClock {
  const g = new Date(now.getTime() + GABORONE_OFFSET_MIN * 60_000);
  const ymd = g.toISOString().slice(0, 10);
  // LKC: the 6-day cycle, not the weekday (School Settings → Teaching Days).
  return {
    day: dayForDate(ymd),
    minutes: g.getUTCHours() * 60 + g.getUTCMinutes(),
    ymd,
  };
}

export const hhmm = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

/** "07:15-07:45" (either dash) → [435, 465], or null. */
export function parseRange(time: string): [number, number] | null {
  const m = /(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/.exec(String(time ?? ''));
  return m ? [Number(m[1]) * 60 + Number(m[2]), Number(m[3]) * 60 + Number(m[4])] : null;
}

/* ============================ where the bell is ============================ */

export type MomentState = 'weekend' | 'before' | 'lesson' | 'break' | 'registration' | 'after' | 'noSchedule';

export interface LiveMoment {
  state: MomentState;
  /** The day shown: today, or the next teaching day when there is no teaching today. */
  day: Day;
  /** The teaching period running now (state 'lesson'), else null. */
  period: number | null;
  /** The period a board shows by default: the one running, else the next, else the first. */
  focus: number;
  /** The current slot of the bell (lesson, break or registration). */
  from?: number;
  to?: number;
  minutesLeft?: number;
  /** 0–1 through the current slot. */
  fraction?: number;
  /** The next teaching period today, if any. */
  next?: { period: number; from: number };
  /** One sentence for the top of the screen. */
  sentence: string;
}

export function liveMoment(schedule: ScheduleSettings | null, clock: SchoolClock): LiveMoment {
  if (!schedule) return { state: 'noSchedule', day: clock.day ?? DAYS[0], period: null, focus: 1, sentence: 'No bell schedule has been published.' };
  if (!clock.day) {
    /* No teaching today (weekend, holiday, break, closure — or the cycle is not
       set up yet): show the next teaching day's first lesson. */
    const next = nextTeachingDay(clock.ymd);
    const day = next?.day ?? DAYS[0];
    const first = schedule.days[day]?.find(isTeachingSlot)?.period ?? 1;
    return {
      state: 'weekend', day, period: null, focus: first,
      sentence: next ? `No lessons today. Here is ${day}, the next teaching day.` : 'No teaching day is set up yet — see School Settings → Teaching Days.',
    };
  }
  const day = clock.day;
  const bell: PeriodSlot[] = schedule.days[day] ?? [];
  const teaching = bell.filter(isTeachingSlot);
  const first = teaching[0]?.period ?? 1;
  const nextAfter = (i: number) => {
    const n = bell.slice(i + 1).find(isTeachingSlot);
    const r = n ? parseRange(n.time) : null;
    return n && r ? { period: n.period, from: r[0] } : undefined;
  };

  for (let i = 0; i < bell.length; i++) {
    const r = parseRange(bell[i].time);
    if (!r || clock.minutes < r[0] || clock.minutes >= r[1]) continue;
    const base = {
      day, from: r[0], to: r[1], minutesLeft: r[1] - clock.minutes,
      fraction: (clock.minutes - r[0]) / Math.max(1, r[1] - r[0]), next: nextAfter(i),
    };
    const ps = bell[i];
    if (isTeachingSlot(ps)) {
      return { ...base, state: 'lesson', period: ps.period, focus: ps.period, sentence: `Period ${ps.period}, until ${hhmm(r[1])}.` };
    }
    const label = ps.period === 'BREAK' ? 'break' : 'registration';
    return {
      ...base, state: label, period: null, focus: base.next?.period ?? first,
      sentence: `It is ${label}${base.next ? `; period ${base.next.period} starts at ${hhmm(base.next.from)}` : ''}.`,
    };
  }
  const start = parseRange(bell[0]?.time ?? '');
  if (start && clock.minutes < start[0]) {
    return { state: 'before', day, period: null, focus: first, next: nextAfter(-1), sentence: `School starts at ${hhmm(start[0])}.` };
  }
  // A gap between two bell slots (lunch not written into the schedule) reads
  // as "before the next one", not as the end of the day.
  const upcoming = bell.map((p, i) => ({ p, i, r: parseRange(p.time) })).find((x) => x.r && x.r[0] > clock.minutes);
  if (upcoming) {
    const n = bell.slice(upcoming.i).find(isTeachingSlot);
    const nr = n ? parseRange(n.time) : null;
    return {
      state: 'break', day, period: null, focus: n?.period ?? first,
      next: n && nr ? { period: n.period, from: nr[0] } : undefined,
      sentence: n && nr ? `Between lessons; period ${n.period} starts at ${hhmm(nr[0])}.` : 'Between lessons.',
    };
  }
  return { state: 'after', day, period: null, focus: first, sentence: 'Lessons are over for today.' };
}

/* ============================ one teacher's day ============================ */

export type RowWhen = 'done' | 'now' | 'later';

export interface DayRow {
  label: string;
  time: string;
  from: number;
  to: number;
  kind: 'lesson' | 'free' | 'break' | 'registration';
  period: number | null;
  slot: TimetableSlot | null;
  /** This teacher's part of the lesson (an option block has several). */
  groups: SlotGroup[];
  when: RowWhen;
}

/** The slot a teacher has at one period of one day, from the slots themselves. */
function teacherSlotAt(teacherId: string, slots: TimetableSlot[], day: Day, period: number): TimetableSlot | null {
  return slots.find((s) => s.day === day && period >= s.period_start && period <= s.period_end
    && slotTeacherIds(s).includes(teacherId)) ?? null;
}

export function teacherDay(
  teacherId: string,
  slots: Record<SlotId, TimetableSlot>,
  schedule: ScheduleSettings | null,
  day: Day,
  clock: SchoolClock | null,
): DayRow[] {
  if (!schedule) return [];
  const all = Object.values(slots);
  const isToday = clock?.day === day;
  return (schedule.days[day] ?? []).map((ps) => {
    const r = parseRange(ps.time) ?? [0, 0];
    const when: RowWhen = !isToday || !clock ? 'later'
      : clock.minutes >= r[1] ? 'done' : clock.minutes >= r[0] ? 'now' : 'later';
    if (!isTeachingSlot(ps)) {
      return {
        label: ps.period === 'BREAK' ? 'Break' : 'Registration', time: ps.time, from: r[0], to: r[1],
        kind: ps.period === 'BREAK' ? 'break' : 'registration', period: null, slot: null, groups: [], when,
      } as DayRow;
    }
    const slot = teacherSlotAt(teacherId, all, day, ps.period);
    const groups = slot ? slotGroups(slot).filter((g) => (g.teacherIds ?? []).includes(teacherId)) : [];
    return {
      label: `P${ps.period}`, time: ps.time, from: r[0], to: r[1],
      // Supervision (GC) is not teaching for a LOAD, but it is where the teacher is.
      kind: slot ? 'lesson' : 'free',
      period: ps.period, slot, groups: groups.length ? groups : slot ? slotGroups(slot) : [], when,
    } as DayRow;
  });
}

/** "Physics_L" with stream "L" → "Physics"; underscores to spaces. */
export function tidySubjectLabel(label?: string | null, stream?: string | null): string | undefined {
  if (!label) return undefined;
  let out = label;
  if (stream && out.endsWith(`_${stream}`)) out = out.slice(0, -(stream.length + 1));
  return out.replace(/_+/g, ' ').trim() || undefined;
}

/** How a screen names a group's subject: the portal's subject name where it
 *  resolves, else the timetable's own label tidied. */
export type SubjectNamer = (g: SlotGroup) => string | undefined;
const defaultNamer: SubjectNamer = (g) => (g.kind === 'supervision' ? 'Supervised study' : tidySubjectLabel(g.subjectLabel, g.streamLabel));

/** What a lesson row says: "AS Physics", "Form 4A Chemistry". */
export function lessonTitle(row: Pick<DayRow, 'slot' | 'groups'>, subjectOf: SubjectNamer = defaultNamer): string {
  if (!row.slot) return 'Free';
  const g = row.groups[0];
  const subject = (g ? subjectOf(g) : undefined)
    ?? tidySubjectLabel(row.slot.subjectLabel ?? row.slot.subject, row.slot.streamLabel) ?? '';
  const classes = compactClasses(g?.classes?.length ? g.classes : row.slot.classes);
  return [classes, subject].filter(Boolean).join(' ') || 'Lesson';
}

/** ["Form 4A","Form 4B","Form 4C"] → "Form 4A, 4B, 4C": an option block names
 *  its whole year group, and three "Form"s in a row is noise on a phone. */
export function compactClasses(classes: string[]): string {
  const out: string[] = [];
  let prev = '';
  for (const c of classes) {
    const m = /^(Form\s*\d)(\S*)$/i.exec(c.trim());
    if (m && m[1] === prev && m[2]) out.push(m[1].replace(/^Form\s*/i, '') + m[2]);
    else out.push(c.trim());
    prev = m ? m[1] : '';
  }
  return out.join(', ');
}

export function lessonRoom(row: Pick<DayRow, 'slot' | 'groups'>): string {
  if (!row.slot) return '';
  const rooms = [...new Set(row.groups.map((g) => g.room).filter(Boolean))];
  return rooms.join(', ') || row.slot.room || '';
}

/* ============================ the whole school ============================ */

export interface BoardTile { cls: string; slot: TimetableSlot | null }

/** Every class and what it has at one period of one day. */
export function classBoard(
  classNames: string[],
  pointers: Record<PointerKey, TimetablePointer>,
  slots: Record<SlotId, TimetableSlot>,
  day: Day,
  period: number,
): BoardTile[] {
  const byClass: Record<string, TimetableSlot> = {};
  for (const [key, ptr] of Object.entries(pointers)) {
    const p = parsePointerKey(key);
    if (!p || p.day !== day || p.period !== period) continue;
    const s = slots[ptr.slotId];
    if (s) byClass[p.cls] = s;
  }
  return classNames.map((cls) => ({ cls, slot: byClass[cls] ?? null }));
}

/** Rooms nobody is timetabled in at one period. A combined label ("BB | C4") is not a room. */
export function freeRoomsAt(rooms: string[], slots: Record<SlotId, TimetableSlot>, day: Day, period: number): string[] {
  const used = new Set<string>();
  for (const s of Object.values(slots)) {
    if (s.day !== day || period < s.period_start || period > s.period_end) continue;
    slotRooms(s).forEach((r) => used.add(r));
  }
  return rooms.filter((r) => !r.includes('|') && !used.has(r));
}

/** Subject family for colour, from the subject's words. */
export type Family = 'sci' | 'maths' | 'lang' | 'hum' | 'arts' | 'other';
export function familyOf(label: string | undefined | null): Family {
  const s = String(label ?? '').toLowerCase();
  if (/(physic|chemist|biolog|science|agricultur)/.test(s)) return 'sci';
  if (/(math|statist)/.test(s)) return 'maths';
  if (/(english|setswana|french|arabic|language|literature|chinese|portug)/.test(s)) return 'lang';
  if (/(history|geograph|religi|islam|social|business|account|economic|sociolog|develop)/.test(s)) return 'hum';
  if (/(art|design|music|drama|physical|pe\b|sport|comput|ict|d&t|technolog)/.test(s)) return 'arts';
  return 'other';
}

export type StaffStatus = 'teaching' | 'free' | 'examDuty' | 'cover';

export interface StaffRow {
  teacherId: string;
  status: StaffStatus;
  /** "AS Physics · Lab 2", or the exam, or '' when free. */
  where: string;
  /** The teacher's next lesson (or duty) later today, or ''. */
  next: string;
  /** One mark per teaching period of the day. */
  day: ('lesson' | 'duty' | 'cover' | 'free')[];
}

/** An exam duty that covers a minute of a day. */
function dutyAt(papers: ExamPaperDuties[], teacherId: string, ymd: string, fromMin: number, toMin: number) {
  for (const p of papers) {
    if (p.date !== ymd) continue;
    for (const d of p.duties ?? []) {
      if (d.teacherId !== teacherId) continue;
      const r = parseRange(`${d.from}-${d.to}`);
      if (r && r[0] < toMin && fromMin < r[1]) return { paper: p, duty: d };
    }
  }
  return null;
}

/** A cover held on the day being looked at (lib/cover.ts), in minutes. */
export interface CoverWindow { teacherId: string; from: number; to: number; label: string }

/**
 * Every teacher's status at one period of one day. Exam duty wins over a
 * timetabled lesson: a teacher in the Hall is not in their classroom, which is
 * exactly the fact the office is looking for. A cover (only ever given in a
 * free period, unless the manager overrode that) comes next.
 */
export function staffAt(
  teacherIds: string[],
  slots: Record<SlotId, TimetableSlot>,
  schedule: ScheduleSettings | null,
  day: Day,
  period: number,
  papers: ExamPaperDuties[],
  ymd: string | null,
  subjectOf: SubjectNamer = defaultNamer,
  covers: CoverWindow[] = [],
): StaffRow[] {
  if (!schedule) return [];
  const coverAt = (id: string, from: number, to: number) => covers.find((c) => c.teacherId === id && c.from < to && from < c.to) ?? null;
  const bell = (schedule.days[day] ?? []).filter(isTeachingSlot);
  const at = bell.find((b) => b.period === period);
  const r = at ? parseRange(at.time) : null;
  const all = Object.values(slots);
  return teacherIds.map((id) => {
    const marks = bell.map((b) => {
      const br = parseRange(b.time);
      if (ymd && br && dutyAt(papers, id, ymd, br[0], br[1])) return 'duty' as const;
      if (br && coverAt(id, br[0], br[1])) return 'cover' as const;
      return teacherSlotAt(id, all, day, b.period) ? 'lesson' as const : 'free' as const;
    });
    const slot = teacherSlotAt(id, all, day, period);
    const row = (s: TimetableSlot | null): string => {
      if (!s) return '';
      const groups = slotGroups(s).filter((g) => (g.teacherIds ?? []).includes(id));
      const dr = { slot: s, groups: groups.length ? groups : slotGroups(s) };
      return [lessonTitle(dr, subjectOf), lessonRoom(dr)].filter(Boolean).join(' · ');
    };
    const duty = ymd && r ? dutyAt(papers, id, ymd, r[0], r[1]) : null;
    let next = '';
    for (const b of bell) {
      if (b.period <= period) continue;
      const br = parseRange(b.time);
      const d = ymd && br ? dutyAt(papers, id, ymd, br[0], br[1]) : null;
      // A duty spanning several periods is not "next" while it is still on.
      if (d && duty && d.paper.id === duty.paper.id) continue;
      if (d) { next = `P${b.period} · exam duty ${d.paper.code}`; break; }
      const s = teacherSlotAt(id, all, day, b.period);
      if (s && s !== slot) { next = `P${b.period} · ${row(s)}`; break; }
    }
    const cover = !duty && r ? coverAt(id, r[0], r[1]) : null;
    if (cover) return { teacherId: id, status: 'cover', where: `Cover: ${cover.label}`, next, day: marks };
    if (duty) {
      return { teacherId: id, status: 'examDuty', where: `Exam: ${duty.paper.code} ${duty.paper.subject} · ${duty.duty.from}–${duty.duty.to}`, next, day: marks };
    }
    return { teacherId: id, status: slot ? 'teaching' : 'free', where: row(slot), next, day: marks };
  });
}

/** Today's exam duties for one teacher, in time order. */
export function dutiesToday(papers: ExamPaperDuties[], teacherId: string, ymd: string) {
  const out: { code: string; subject: string; from: string; to: string; role: string }[] = [];
  for (const p of papers) {
    if (p.date !== ymd) continue;
    for (const d of p.duties ?? []) {
      if (d.teacherId === teacherId) out.push({ code: p.code, subject: p.subject, from: d.from, to: d.to, role: d.role });
    }
  }
  return out.sort((a, b) => a.from.localeCompare(b.from));
}
